import React, { Fragment, useEffect, useState } from 'react'
import { useCompanyName } from '@src/utils/useCompanyName'
import clsx from 'clsx'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import {
  Theme,
  Typography,
  Divider,
  CircularProgress,
  TooltipProps,
} from '@material-ui/core'
import {
  cartRoute,
  CartType,
  CartView,
  useRouter,
  ActiveMiniCartType,
} from '@src/routes'
import { QuickCartQuery } from '@src/queries/QuickCartQuery.generated'
import ClearIcon from '@material-ui/icons/Clear'
import { makeStyles } from '@material-ui/core/styles'
import Drawer from '@material-ui/core/Drawer'
import ButtonBase from '@material-ui/core/ButtonBase'
import Button from '@material-ui/core/Button'
import MiniCartItem from './MiniCartItem'
import cartMessages from '@src/routes/Cart/messages'
import messages from '@utils/messages'
import { sendViewShoppingCartEvent } from '@utils/analytics/cartAndCheckoutEvents'
import { EventValues } from '@sial/common-utils'
import ClearCartItemsDialog from '../ClearCartItemsDialog'
import { groupBy } from 'lodash'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import useCartItemsPriceAvailability from '@src/routes/Cart/useCartItemsPriceAvailability'
import { useUpdateQuickCartItems } from '@src/utils/useCart'
import { getCartMiniAction } from '@src/utils/cartUtils'
import { CartItemType } from '@src/types/graphql-types'
import CommonTabs, { CommonTab } from '@src/components/Common/CommonTabs'
import countriesMap from '@localization/countries.json'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'
import isMarketplaceType from '@src/utils/isMarketplaceType'

const useStyles = makeStyles((theme: Theme) => ({
  drawer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflowX: 'hidden',
    [theme.breakpoints.up('sm')]: {
      width: 540,
    },
  },
  paper: {
    left: 0,
    [theme.breakpoints.up('sm')]: {
      left: 'auto',
      right: 0,
    },
  },
  header: {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(4),
  },
  closeIcon: {
    fontSize: 26,
  },
  body: {
    height: '100%',
    overflow: 'auto',
    backgroundColor: theme.palette.background.grey,
  },
  empty: {
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(4),
  },
  footer: {
    flexShrink: 0,
    padding: theme.spacing(5, 4, 4),
    boxShadow: '-1px 0 7px rgb(50 50 50 / 75%)',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(5, 8, 4),
    },
  },
  actionDivider: {
    backgroundColor: theme.palette.grey[400],
    margin: theme.spacing(2, 0),
  },
  checkoutDivider: {
    backgroundColor: theme.palette.grey[400],
    margin: theme.spacing(2, 0, 4),
  },
  tabButton: {
    flex: 1,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: '1.22',
    letterSpacing: '0.01px',
    textAlign: 'left',
    paddingBottom: theme.spacing(1),
    borderBottom: '4px solid transparent',
    color: theme.palette.primary.main,
    [theme.breakpoints.up(380)]: {
      fontSize: theme.typography.pxToRem(18),
    },
    [theme.breakpoints.down(322)]: {
      padding: theme.spacing(4, 2, 0, 2),
    },
    [theme.breakpoints.up(322)]: {
      padding: theme.spacing(4, 4, 2, 4),
    },
  },
  activeTab: {
    borderBottom: `4px solid ${theme.palette.common.black}`,
    color: theme.palette.common.black,
  },
  items: {
    backgroundColor: theme.palette.common.white,
    '& > *': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
  },
  linkContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(4),
  },
  linkText: {
    fontWeight: theme.typography.fontWeightBold,
  },
  needAQuoteLink: {
    display: 'block',
    fontSize: theme.typography.pxToRem(16),
    textAlign: 'center',
    marginTop: theme.spacing(6),
  },
  estimatedTotalContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(16),
  },
  subTitle: {
    color: theme.palette.grey[500],
    fontSize: theme.typography.pxToRem(12),
    marginTop: theme.spacing(1),
  },
}))

const useToolTipStyles = makeStyles((theme) => ({
  arrow: {
    color: theme.palette.common.white,
  },

  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(4),
    boxShadow: '0 4px 6px 0 rgba(0, 0, 0, 0.16)',
  },
}))

export interface MiniCartDrawerProps {
  activeCartType: ActiveMiniCartType
  cart: QuickCartQuery['getCart']
  cartType: CartType
  open: boolean
  marketplaceCount: number
  blueCartCount: number
  sialCount: number
  closeMiniCart: () => void
  toggleCart: (activeCartType: ActiveMiniCartType) => void
}

const MiniCartDrawer: React.FC<MiniCartDrawerProps> = ({
  activeCartType,
  cart,
  cartType,
  open,
  marketplaceCount,
  blueCartCount,
  sialCount,
  closeMiniCart,
  toggleCart,
}) => {
  const companyName = useCompanyName()
  const router = useRouter()
  const classes = useStyles()
  const toolTipClasses = useToolTipStyles()
  const { userIsLoggedIn, hasOnlyBlueERP } = useCurrentUser()
  const { formatMessage } = useIntl()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [showTotal, setShowTotal] = useState(false)
  const [itemId, setItemId] = useState('')
  const isMarketplace = isMarketplaceType(cartType)
  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)
  const hasMultipleCarts = marketplaceCount > 0 || blueCartCount > 0
  const groupedMarketPlaceCartItems = groupBy(cart?.items, (item) =>
    isMarketplace
      ? item.material?.displaySellerName ||
        item.brand ||
        item.material?.brand?.name
      : ''
  )

  // max number of items that will fetch pricing info
  const DEFAULT_LIMIT = 10

  const [updateQuickCartItems, { loading: updateLoading }] =
    useUpdateQuickCartItems()

  const {
    data: priceData,
    loading: priceLoading,
    error: priceError,
    subTotal,
    currency,
  } = useCartItemsPriceAvailability(cart?.items, cartType, DEFAULT_LIMIT)

  const closeCancelCartDialog = (isConfirm = false) => {
    setDialogOpen(false)
    if (isConfirm) {
      closeMiniCart()
    }
  }

  const handleUpdateQuantity = async (itemId: string, value: number) => {
    setItemId(itemId)

    updateQuickCartItems({
      variables: {
        input: {
          cartItemId: itemId,
          quantity: value,
        },
        action: getCartMiniAction(cartType),
      },
    })
  }

  const handleViewCartButtonClick = () => {
    sendViewShoppingCartEvent({
      isMarketplaceCart: isMarketplace,
      detail: `view ${isMarketplace ? 'marketplace' : 'standard'} cart`,
      section: 'mini cart',
      component: 'slider',
    })
    router.push(cartRoute[CartView.Cart](cartType))
    closeMiniCart()
  }

  // validation used for showing the estimated total
  useEffect(() => {
    const showPricing = Object.entries(groupedMarketPlaceCartItems)?.every(
      ([, sellerCartItems]) =>
        sellerCartItems?.every(
          (item) =>
            item.type !== CartItemType.Configurator &&
            !item.quoteNumber &&
            !item.promoCode
        )
    )

    // if any item has hidePriceMessageKey, then hide the total
    const hidePriceMessageKey = Object.values(priceData).some(
      (item) => item?.materialPriceAvailability?.hidePriceMessageKey
    )

    // if any item has displayInquireLink, then hide the total
    const availabilities = Object.values(priceData).flatMap(
      (item) => item?.materialPriceAvailability?.availabilities || []
    )
    const hasInquireLink = availabilities.some(
      (item) => item?.displayInquireLink
    )

    setShowTotal(
      cart?.items?.length <= DEFAULT_LIMIT &&
        showPricing &&
        !priceLoading &&
        !priceError &&
        !hasInquireLink &&
        !hidePriceMessageKey
    )
  }, [
    priceLoading,
    priceError,
    cart?.items,
    groupedMarketPlaceCartItems,
    priceData,
  ])

  const getToolTip = (
    title: string,
    disableToolTip?: boolean, // used if we need to disable it for some users
    arrow?: boolean,
    placement?: TooltipProps['placement']
  ) => {
    if (disableToolTip) return

    return {
      title: title,
      arrow: arrow || false,
      placement: placement || 'bottom',
      classes: toolTipClasses,
    } as CommonTab['toolTip']
  }

  const CartItems: React.FC = () => (
    <div className={classes.body}>
      {cart && cart.items?.length ? (
        <div className={clsx(classes.items)}>
          {Object.entries(groupedMarketPlaceCartItems)?.map(
            ([, sellerCartItems], index) => (
              <Fragment key={`groupedMarketItems-${index}`}>
                {sellerCartItems?.map((item, index) => (
                  <MiniCartItem
                    key={item.id}
                    item={item}
                    cart={cart}
                    cartType={cartType}
                    applyGroupedHeading={!index}
                    closeMiniCart={closeMiniCart}
                    handleUpdateQuantity={handleUpdateQuantity}
                    priceData={priceData}
                    loading={
                      itemId === item.id ? priceLoading || updateLoading : false
                    }
                    error={priceError}
                  />
                ))}
              </Fragment>
            )
          )}
        </div>
      ) : (
        <div className={classes.empty}>
          <FormattedMessage {...messages.YOUR_CART_IS_EMPTY} />
        </div>
      )}
    </div>
  )

  // Creates tabs based on cart counts
  const MiniCartTabs: React.FC = () => {
    const createTabs = (): (CommonTab & { type: ActiveMiniCartType })[] => {
      // Tab labels
      const marketplaceName = formatMessage({
        id: 'MARKETPLACE_ALIAS',
        defaultMessage: 'Marketplace',
      })
      const blueCartName = formatMessage({
        id: 'EMERALD_ALIAS',
        defaultMessage: 'Merck Chemicals',
      })
      const sialLabel = `${companyName} (${sialCount})`
      const marketplaceLabel = `${marketplaceName} (${marketplaceCount})`
      const blueCartLabel = `${blueCartName} (${blueCartCount})`

      const tabs: (CommonTab & { type: ActiveMiniCartType })[] = []

      tabs.push({
        type: ActiveMiniCartType.SIAL,
        label: sialLabel,
        selectCallback: () => toggleCart(ActiveMiniCartType.SIAL),
        component: <CartItems />,
        toolTip: getToolTip(
          formatMessage({
            id: 'COMPANYNAME_SIGMA_CHINA',
            defaultMessage: 'Sigma-Aldrich (Shanghai) Trading Co., Ltd.',
          }),
          !isChinaUser
        ),
      })

      if (blueCartCount > 0) {
        tabs.push({
          type: ActiveMiniCartType.BLUECART,
          label: blueCartLabel,
          selectCallback: () => toggleCart(ActiveMiniCartType.BLUECART),
          component: <CartItems />,
          toolTip: getToolTip(
            formatMessage({
              id: 'COMPANYNAME_MERCK_CHEMICALS_CHINA',
              defaultMessage: 'Merck Chemicals (Shanghai) Co., Ltd.',
            }),
            !isChinaUser
          ),
        })
      }

      if (marketplaceCount > 0) {
        tabs.push({
          type: ActiveMiniCartType.MARKETPLACE,
          label: marketplaceLabel,
          selectCallback: () => toggleCart(ActiveMiniCartType.MARKETPLACE),
          component: <CartItems />,
        })
      }

      return tabs
    }

    const tabs = createTabs()
    const activeTabIndex = tabs.findIndex((tab) => tab.type === activeCartType)
    return <CommonTabs tabs={tabs} activeTabIndex={activeTabIndex} />
  }

  // Resets the cart type if there are no split cart items
  useEffect(() => {
    if (hasOnlyBlueERP) return

    if (!hasMultipleCarts && activeCartType !== ActiveMiniCartType.SIAL) {
      toggleCart(ActiveMiniCartType.SIAL)
    }
  }, [hasMultipleCarts, hasOnlyBlueERP])

  return (
    <Drawer
      classes={{ paperAnchorRight: classes.paper }}
      anchor="right"
      open={open}
      onClose={closeMiniCart}
    >
      <div className={classes.drawer} data-testid="mini-cart-drawer">
        <div className={classes.header}>
          <div className={classes.title}>
            <Typography variant="h2" component="h2">
              <FormattedMessage {...messages.CART} />
              {!hasMultipleCarts && !!sialCount && <span>({sialCount})</span>}
            </Typography>
            <ButtonBase onClick={closeMiniCart} data-testid="mini-cart-close">
              <ClearIcon className={classes.closeIcon} />
            </ButtonBase>
          </div>
          {hasMultipleCarts && !hasOnlyBlueERP && <MiniCartTabs />}
        </div>

        {(!hasMultipleCarts || hasOnlyBlueERP) && <CartItems />}

        {cart && cart.items.length > 0 && (
          <div className={classes.footer}>
            <div className={classes.estimatedTotalContainer}>
              <Typography variant="inherit">
                <FormattedMessage {...cartMessages.ESTIMATED_TOTAL} />
              </Typography>
              {showTotal && subTotal ? (
                <FormattedNumber
                  style="currency"
                  currencyDisplay="symbol"
                  currency={currency}
                  value={subTotal}
                />
              ) : priceLoading ? (
                <CircularProgress size={18} />
              ) : (
                <Typography>
                  <FormattedMessage {...messages.VIEW_CART_FOR_PRICING} />
                </Typography>
              )}
            </div>

            <div className={classes.subTitle}>
              <FormattedMessage {...messages.TOTAL_SUBTITLE} />
            </div>

            <Divider className={classes.checkoutDivider} />

            <Button
              size="large"
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleViewCartButtonClick}
              data-testid="mini-cart-checkout"
              id="mini-cart-checkout"
            >
              <FormattedMessage id="VIEW_CART" defaultMessage="View Cart" />
            </Button>

            {cart && cart.items.length ? (
              <div className={classes.linkContainer}>
                <Button
                  className={classes.linkText}
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  data-testid="mini-cart-clear-cart"
                  id="mini-cart-clear-cart"
                >
                  <FormattedMessage
                    id="CLEAR_ALL_CART_ITEMS"
                    defaultMessage="Clear Cart"
                  />
                </Button>
              </div>
            ) : null}

            {activeCartType === ActiveMiniCartType.SIAL && !userIsLoggedIn && (
              <div>
                <a
                  href="https://maestro.my.site.com/knowledgeportal/s/article/How-do-I-create-a-Quote-also-called-Proforma-Invoice"
                  className={classes.needAQuoteLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage
                    id="NEED_QUOTE_LEARN_MORE"
                    defaultMessage="Need a Quote? Learn More"
                  />
                </a>
              </div>
            )}
          </div>
        )}
      </div>
      <ClearCartItemsDialog
        open={dialogOpen}
        onClose={closeCancelCartDialog}
        cartType={cartType}
        items={cart?.items}
        cartPage={EventValues.Slider}
        marketplaceCart={isMarketplace}
        currency={currency}
      />
    </Drawer>
  )
}
export default MiniCartDrawer
